import * as React from 'react';
import { DefaultButton, MessageBar, MessageBarType, Panel, PanelType, PrimaryButton, ProgressIndicator, Stack, Text } from '@fluentui/react';
import { UserPropertiesView } from './UserPropertiesView';
import { IUserContactInfo } from '../../../../Models/Admin/IUserContactInfo';
import { UserPropertiesContactInformation } from './UserPropertiesContactInformation';
import { UserPropertiesInvoiceInformation } from './UserPropertiesInvoiceInformation';

import { useBoolean } from '@fluentui/react-hooks';
import { IUserInvoiceInfo } from '../../../../Models/Admin/IUserInvoiceInfo';
import * as Services from '../../../../Services/Services';
import { IStatusMessage } from '../../../../Models/IStatusMessage';
import { IAdminApi } from '../../../../Services/API/Admin/IAdminApi';
import { UserPropertiesRoles } from './UserPropertiesRoles';
import { IUserRoles } from '../../../../Models/Admin/IUserRoles';
import { connect } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { IAppStore } from '../../../../Stores/IAppStore';
import { AnyAction } from 'redux';
import { IUserProfile } from '../../../../Models/Profile/IUserProfile';


interface IUserPropertiesProps {
    userId?: string;
    refresh: () => void;
    profile: IUserProfile;
}

const defultContactInfo: IUserContactInfo = {
    city: "",
    email: "",
    firstName: "",
    lastName: "",
    id: "",
    phoneNumber: "",
    postalCode: "",
    street: "", emailConfirmed: false, isContactInformationCompleated: false, isPrivateInformationConsent: false, isActive: false
}

const defultInvoiceInfo: IUserInvoiceInfo = {
    billingCity: "",
    billingEmail: "",
    id: "",
    
    billingPostalCode: "",
    billingStreet: "",
    company: "",
    dic: "",
    ic: "",
    differentBillingAddress: false,
    isActive: false,
}

export const UserProperties: React.FunctionComponent<IUserPropertiesProps> = (props) => {

    const [userContactInfo, setUserContactInfo] = React.useState(defultContactInfo)
    const [userInvoiceInfo, setInvoiceInfo] = React.useState<IUserInvoiceInfo>(defultInvoiceInfo)
    const [userRoles, setUserRoles] = React.useState<IUserRoles>({ accountant: false, admin:false,teacher:false })

    const [userContactInfoPanel, setUserContactInfoPanel] = useBoolean(false);
    const [userInvoiceInfoPanel, setUserInvoiceInfoPanel] = useBoolean(false);
    const [userRoleInfoPanel, setUserRoleInfoPanel] = useBoolean(false);
    
    const [isLoading, setIsLoading] = useBoolean(false);
    const [statusMessage, setStatusMessage] = React.useState<IStatusMessage>()

    const [userContactInfoValid, setUserContactInfoValid] = React.useState(false);
    const [userInvoiceInfoValid, setUserInvoiceInfoValid] = React.useState(false);

    const changeUserState = async (id: string, isActive: boolean) => {
        setIsLoading.setTrue();
        setStatusMessage(undefined);

        const adminApi = Services.ServicesProvider.GetService<IAdminApi>(Services.AdminApi);
        if (props.userId) {
            const userInfo = await adminApi.changeUserState(id, isActive);
            if (userInfo.isSuccess) {
                setStatusMessage({ message: userInfo.message, type: MessageBarType.success });
                await loadDataUserInfo();
            }
            else {
                setStatusMessage({ message: userInfo.message, type: MessageBarType.error });
            }
        }
        setIsLoading.setFalse();
    }

    const loadDataUserInfo = async () => {
        setIsLoading.setTrue();
        setStatusMessage(undefined);

        const adminApi = Services.ServicesProvider.GetService<IAdminApi>(Services.AdminApi);
        if (props.userId) {
            const userInfo = await adminApi.getUserContactInfo(props.userId);

            if (userInfo.isSuccess && userInfo.data) {
                setStatusMessage({ message: userInfo.message, type: MessageBarType.success });
                setUserContactInfo(userInfo.data);
                props.refresh();
            }
            else {
                setStatusMessage({ message: userInfo.message, type: MessageBarType.error });
            }
        }
        setIsLoading.setFalse();
    }

    const loadDataInvoiceInfo = async () => {
        setIsLoading.setTrue();
        setStatusMessage(undefined);

        const adminApi = Services.ServicesProvider.GetService<IAdminApi>(Services.AdminApi);
        if (props.userId) {
            const invoiceInfo = await adminApi.getUserInvoiceInfo(props.userId);

            if (invoiceInfo.isSuccess && invoiceInfo.data) {
                setStatusMessage({ message: invoiceInfo.message, type: MessageBarType.success });
                setInvoiceInfo(invoiceInfo.data);
            }
            else {
                setStatusMessage({ message: invoiceInfo.message, type: MessageBarType.error });
            }
        }
        setIsLoading.setFalse();
    }

    const loadDataUserRoles = async () => {
        setIsLoading.setTrue();
        setStatusMessage(undefined);

        const adminApi = Services.ServicesProvider.GetService<IAdminApi>(Services.AdminApi);
        if (props.userId) {
            const roles = await adminApi.getUserRoles(props.userId);

            if (roles.isSuccess && roles.data) {
                setStatusMessage({ message: roles.message, type: MessageBarType.success });
                setUserRoles(roles.data);
            }
            else {
                setStatusMessage({ message: roles.message, type: MessageBarType.error });
            }
        }
        setIsLoading.setFalse();
    }
    

    React.useEffect(() => {
        loadDataUserInfo();
    }, [])

    React.useEffect(() => {
        if (userInvoiceInfoPanel)
           loadDataInvoiceInfo();
    }, [userInvoiceInfoPanel])

    React.useEffect(() => {
        if (userContactInfoPanel)
            loadDataUserInfo();
    }, [userContactInfoPanel])


    React.useEffect(() => {
        if (userRoleInfoPanel)
            loadDataUserRoles();
    }, [userRoleInfoPanel])


    const contactInfoPropertyChanged = (key: string, value: any) => {
        setUserContactInfo({ ...userContactInfo, [key]: value });
    }

    const contactInvoicePropertyChanged = (key: string, value: any) => {
        if (key !== "differentBillingAddress" && value !== true) {
            setInvoiceInfo({ ...userInvoiceInfo, [key]: value });
        }
        else {
            setInvoiceInfo({
                billingCity: userContactInfo.city,
                billingEmail: userContactInfo.email,
                billingPostalCode: userContactInfo.postalCode,
                billingStreet: userContactInfo.street,
                company: "",
                dic: "",
                ic: "",
                id: userContactInfo.id,
                differentBillingAddress: value,
                isActive: userContactInfo.isActive,
            });
        }
    }

    const roleInfoPropertyChanged = (key: string, value: any) => {
        setUserRoles({ ...userRoles, [key]: value });
    }


    const editContactInfo = (id: string)=>
    {
        setUserContactInfoPanel.setTrue();
    }

    const editInvoiceInfo = (id: string) => {
        setUserInvoiceInfoPanel.setTrue();
    }

    const editUserRoles = (id: string) => {
        setUserRoleInfoPanel.setTrue();
    }
    

    const resetPassword = (id: string) => {

    }

    const InvoiceInformationSave = async () => {
        setIsLoading.setTrue();
        setStatusMessage(undefined);

        const adminApi = Services.ServicesProvider.GetService<IAdminApi>(Services.AdminApi);
        const invoiceInfo = await adminApi.saveUserInvoiceInfo(userInvoiceInfo);

        if (invoiceInfo.isSuccess) {
            setStatusMessage({ message: invoiceInfo.message, type: MessageBarType.success });
            setUserInvoiceInfoPanel.setFalse();
        }
        else {
            setStatusMessage({ message: invoiceInfo.message, type: MessageBarType.error });            
        }
        setIsLoading.setFalse();
    }

    const ContactInformationSave =async () => {
        setIsLoading.setTrue();
        setStatusMessage(undefined);

        const adminApi = Services.ServicesProvider.GetService<IAdminApi>(Services.AdminApi);
        const contactInfo = await adminApi.saveUserContactInfo(userContactInfo);

        if (contactInfo.isSuccess) {
            setStatusMessage({ message: contactInfo.message, type: MessageBarType.success });
            setUserContactInfoPanel.setFalse();           
        }
        else {
            setStatusMessage({ message: contactInfo.message, type: MessageBarType.error });
        }
        setIsLoading.setFalse();
    }

    const RolesSave = async () => {
        setIsLoading.setTrue();
        setStatusMessage(undefined);

        const adminApi = Services.ServicesProvider.GetService<IAdminApi>(Services.AdminApi);
        const contactInfo = await adminApi.addUserToRoles(props.userId ? props.userId:"", userRoles);

        if (contactInfo.isSuccess) {
            setStatusMessage({ message: contactInfo.message, type: MessageBarType.success });
            setUserContactInfoPanel.setFalse();
        }
        else {
            setStatusMessage({ message: contactInfo.message, type: MessageBarType.error });
        }
        setIsLoading.setFalse();
    }

    const sendNewRegistrationEmail = async (userId: string) => {
        const adminApi = Services.ServicesProvider.GetService<IAdminApi>(Services.AdminApi);
        const contactInfo = await adminApi.sendNewRegistrationEmail(props.userId ? props.userId : "");
        if (contactInfo.isSuccess) {
            setStatusMessage({ message: "Registrační email byl úspěšně odeslán", type: MessageBarType.success })
        }
        else {
            setStatusMessage({ message: contactInfo.message, type: MessageBarType.error });
        }
    }

    
    const sendNewRegistrationEmailWithPassword = async (userId: string) => {
        const adminApi = Services.ServicesProvider.GetService<IAdminApi>(Services.AdminApi);
        const contactInfo = await adminApi.sendNewRegistrationEmailWithPassword(props.userId ? props.userId : "");
        if (contactInfo.isSuccess) {
            setStatusMessage({ message: "Registrační email byl úspěšně odeslán", type: MessageBarType.success })
        }
        else {
            setStatusMessage({ message: contactInfo.message, type: MessageBarType.error });
        }
    }



    return (
        <Stack>
            <UserPropertiesView isLoading={isLoading}
                editContactInfo={editContactInfo}
                editInvoiceInfo={editInvoiceInfo}
                editUserRoles={editUserRoles}
                resetPassword={resetPassword}
                userContactInfo={userContactInfo}
                sendNewRegistrationEmail={sendNewRegistrationEmail}
                sendNewRegistrationEmailWithPassword={sendNewRegistrationEmailWithPassword}
                changeUserState={changeUserState}
                profile={props.profile}
            />
            {statusMessage && statusMessage.message != "" ? <MessageBar messageBarType={statusMessage.type}>{statusMessage.message}</MessageBar> : null}
            {userContactInfoPanel ?
                <Panel
                    isOpen={userContactInfoPanel}
                    onDismiss={setUserContactInfoPanel.setFalse}
                    type={PanelType.medium}
                    closeButtonAriaLabel="Zavřít">
                    <Stack horizontal>
                       <Stack horizontal verticalAlign="center">
                            <Text styles={{ root: { fontWeight: 600 } }} variant="xLargePlus">
                                Spravovat kontaktní informace
                            </Text>
                        </Stack>
                    </Stack>
                    <UserPropertiesContactInformation
                        userContactInfo={userContactInfo}
                        statusMessage={statusMessage}
                        isLoading={isLoading} changeInfoProperty={contactInfoPropertyChanged}
                        valid={setUserContactInfoValid}
                    />
                    <Stack styles={{ root: { height: 10 } }}>
                        {isLoading ? <ProgressIndicator /> : null}
                    </Stack>

                    <Stack horizontal tokens={{ padding: 20, childrenGap: 15 }} horizontalAlign="center">
                        <PrimaryButton disabled={!userContactInfoValid} onClick={ContactInformationSave}>Uložit</PrimaryButton>
                        <DefaultButton onClick={setUserContactInfoPanel.setFalse}>Zavřít</DefaultButton>
                    </Stack>
                    
                    {statusMessage && statusMessage.message != undefined ? <MessageBar messageBarType={statusMessage.type}>{statusMessage.message}</MessageBar> : null}
                </Panel> : null}
            {userInvoiceInfoPanel ?
                <Panel
                    isOpen={userInvoiceInfoPanel}
                    onDismiss={setUserInvoiceInfoPanel.setFalse}
                    type={PanelType.medium}
                    closeButtonAriaLabel="Zavřít">
                    <Stack horizontal>


                        <Stack horizontal verticalAlign="center">
                            <Text styles={{ root: { fontWeight: 600 } }} variant="xLargePlus">
                                Spravovat fakturační informace
                            </Text>
                        </Stack>
                    </Stack>
                    <UserPropertiesInvoiceInformation
                        userInvoceInfo={userInvoiceInfo}
                        statusMessage={statusMessage}
                        isLoading={isLoading} changeInfoProperty={contactInvoicePropertyChanged}
                        valid={setUserInvoiceInfoValid}
                    />


                    <Stack styles={{ root: { height: 10 } }}>
                        {isLoading ? <ProgressIndicator /> : null}
                    </Stack>
                    <Stack horizontal tokens={{ padding: 20, childrenGap: 15 }} horizontalAlign="center">
                        <PrimaryButton disabled={!userInvoiceInfoValid} onClick={InvoiceInformationSave}>Uložit</PrimaryButton>
                        <DefaultButton onClick={setUserInvoiceInfoPanel.setFalse}>Zavřít</DefaultButton>
                    </Stack>
                    {statusMessage && statusMessage.message ? <MessageBar messageBarType={statusMessage.type}>{statusMessage.message}</MessageBar> : null}



                </Panel> : null}
            {userRoleInfoPanel ?
                <Panel
                    isOpen={userRoleInfoPanel}
                    onDismiss={setUserRoleInfoPanel.setFalse}
                    type={PanelType.medium}
                    closeButtonAriaLabel="Zavřít">                   
                    <UserPropertiesRoles
                        roles={userRoles}
                        statusMessage={statusMessage}
                        isLoading={isLoading}
                        changeInfoProperty={roleInfoPropertyChanged}
                        onDismiss={setUserRoleInfoPanel.setFalse}
                        onSave={RolesSave}
                    />
                </Panel> : null}
            
        </Stack>
    )
}


const UserPropertiesChangeToProps = (state: IAppStore) => ({
    profile: state.userProfileStore.profile,


});

const UserPropertiesChangeDispatchToProps = (dispatch: ThunkDispatch<IAppStore, any, AnyAction>) => ({

});

export default connect(UserPropertiesChangeToProps, UserPropertiesChangeDispatchToProps)(UserProperties);
